import axios from 'axios';
import { Regional, HistoryEntry, RegionalChange } from './types';

const client = axios;

type FetchResponse = {
  success: boolean;
  data: {
    IDDistribuidor: string | number | null;
    NomeCompletoDistribuidor: string | null;
    Estado: string | null;
    regional_ul: string | null;
  }[];
};

export async function fetchRegionais(): Promise<Regional[]> {
  const { data } = await client.get<FetchResponse>('/regional-ul');
  const rows = data?.data ?? [];
  return rows.map((row) => ({
    idDistribuidor: String(row.IDDistribuidor ?? ''),
    nomeCompletoDistribuidor: String(row.NomeCompletoDistribuidor ?? ''),
    regiaoUL: String(row.regional_ul ?? ''),
    estado: String(row.Estado ?? ''),
  }));
}

type BatchResultItem = {
  index: number;
  ok: boolean;
  id_distribuidor: string | number | null;
  error?: string;
};

type BatchResponse = {
  success: boolean;
  count?: number;
  changedCount?: number;
  results?: BatchResultItem[];
  error?: string;
};

export async function batchUpdateRegionais(
  items: { id_distribuidor: string; regional_ul: string }[]
): Promise<BatchResponse> {
  const { data } = await client.post<BatchResponse>(
    '/regional-ul/list',
    { items }
  );
  return data;
}

type HistoryRow = {
  regional_ul_distribuidor_history_id: string;
  regional_ul_distribuidor_id: string;
  user_id: string;
  name: string | null;
  email: string | null;
  IDDistribuidor: string | null;
  NomeCompletoDistribuidor: string | null;
  Estado: string | null;
  data: string | null;
  created_at: string;
};

type HistoryApiRaw = {
  success: boolean;
  data: {
    success: boolean;
    columns: string[];
    data: string[][];
  };
};

function parseHistoryRows(raw: HistoryApiRaw): HistoryRow[] {
  const cols = raw?.data?.columns ?? [];
  const rows = raw?.data?.data ?? [];
  return rows.map((arr) => {
    const obj: Record<string, string | null> = {};
    cols.forEach((col, i) => {
      obj[col] = arr[i] ?? null;
    });
    return obj as unknown as HistoryRow;
  });
}

type ChangeItem = {
  id_distribuidor: number | string;
  before: { regional_ul?: string } | null;
  after: { regional_ul?: string } | null;
};

type ParsedData = {
  type?: string;
  action?: string;
  total_changed?: number;
  changes?: ChangeItem[];
  before?: { regional_ul?: string } | null;
  after?: { regional_ul?: string } | null;
  id_distribuidor?: number | string;
};

function extractChanges(
  parsed: ParsedData,
  row: HistoryRow,
  regionaisMap: Map<string, Regional>
): RegionalChange[] {
  if (Array.isArray(parsed.changes)) {
    return parsed.changes.map((c) => {
      const distId = String(c.id_distribuidor ?? row.IDDistribuidor ?? '');
      const regional = regionaisMap.get(distId);
      return {
        idDistribuidor: distId,
        nomeDistribuidor: regional?.nomeCompletoDistribuidor || String(row.NomeCompletoDistribuidor ?? distId),
        valorAnterior: c.before?.regional_ul ?? '',
        valorNovo: c.after?.regional_ul ?? '',
      };
    });
  }

  const distId = String(parsed.id_distribuidor ?? row.IDDistribuidor ?? '');
  const regional = regionaisMap.get(distId);
  return [{
    idDistribuidor: distId,
    nomeDistribuidor: regional?.nomeCompletoDistribuidor || String(row.NomeCompletoDistribuidor ?? distId),
    valorAnterior: parsed.before?.regional_ul ?? '',
    valorNovo: parsed.after?.regional_ul ?? '',
  }];
}

export async function fetchRegionaisHistory(): Promise<HistoryEntry[]> {
  const [historyRes, regionaisRes] = await Promise.all([
    client.get<HistoryApiRaw>('/regional-ul/history'),
    client.get<FetchResponse>('/regional-ul').catch(() => null),
  ]);

  const regionaisMap = new Map<string, Regional>();
  if (regionaisRes?.data?.data) {
    for (const row of regionaisRes.data.data) {
      const key = String(row.IDDistribuidor ?? '');
      regionaisMap.set(key, {
        idDistribuidor: key,
        nomeCompletoDistribuidor: String(row.NomeCompletoDistribuidor ?? ''),
        regiaoUL: String(row.regional_ul ?? ''),
        estado: String(row.Estado ?? ''),
      });
    }
  }

  const rows = parseHistoryRows(historyRes.data);

  const groupedMap = new Map<string, {
    date: string;
    userName: string;
    changes: RegionalChange[];
  }>();

  for (const row of rows) {
    const userName = row.name || row.email || 'Desconhecido';
    const rawData = row.data || '{}';
    const createdAt = row.created_at || '';

    let parsed: ParsedData = {};
    try {
      parsed = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
    } catch {
      continue;
    }

    const changes = extractChanges(parsed, row, regionaisMap);
    const groupKey = `${createdAt}__${userName}`;

    const existing = groupedMap.get(groupKey);
    if (existing) {
      existing.changes.push(...changes);
    } else {
      groupedMap.set(groupKey, {
        date: createdAt,
        userName,
        changes,
      });
    }
  }

  const entries: HistoryEntry[] = [];
  let idx = 0;
  for (const [, value] of groupedMap) {
    entries.push({
      id: `history-${idx++}`,
      date: value.date,
      user: value.userName,
      changes: value.changes,
    });
  }

  return entries;
}
